import { forwardRef } from 'react'
import classNames from 'classnames'

import { componentsWebColors } from '@foxino/components-web'
import { baseColors } from '@foxino/configs/tailwind/baseColors'

type SizeType = 'small' | 'big'

type Props = Omit<React.HTMLProps<HTMLInputElement>, 'inputMode' | 'size' | 'value' | 'onChange'> & {
  label?: string
  description?: string
  size?: SizeType
  indeterminate?: boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement>
}

/**
 * @group Components
 * @category Component
 */
export const RadioButton = forwardRef(
  (
    { className, description, label, disabled, name = 'radio-group', onChange, size = 'small', ...restProps }: Props,
    ref: React.Ref<HTMLInputElement>
  ) => {
    const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      event.stopPropagation()
      onChange?.(event)
    }

    return (
      <div className={classNames('flex', className)}>
        <input
          id={`inline-radio/${label}`}
          name={name}
          type="radio"
          onChange={handleOnChange}
          disabled={disabled}
          className={classNames(
            'peer appearance-none rounded-full border border-neutral-300',
            size === 'small' ? 'ml-[-1px] mt-[-1px] h-4 w-4' : '-ml-0.5 -mt-0.5 h-5 w-5',
            !disabled &&
              'hover:bg-primary-25 checked:border-primary-500 checked:bg-primary-500 hover:border-primary-600 hover:checked:bg-primary-600 active:border-2 active:bg-white',
            disabled
              ? 'cursor-not-allowed border-neutral-200 bg-neutral-100 checked:border-neutral-200 checked:bg-neutral-100'
              : 'cursor-pointer'
          )}
          ref={ref}
          {...restProps}
        />
        {label && (
          <div className={classNames('ml-2 mt-[-3px] flex flex-col text-sm')}>
            {label && (
              <label
                htmlFor={`inline-radio/${label}`}
                className={classNames(
                  !disabled ? 'text-neutral-900' : 'text-neutral-400',
                  description && 'font-semibold'
                )}
              >
                {label}
              </label>
            )}
            {description && <span className="text-neutral-500">{description}</span>}
          </div>
        )}
        <svg
          className="pointer-events-none absolute ml-1 mt-1 hidden peer-checked:block"
          width={size === 'small' ? 6 : 8}
          height={size === 'small' ? 6 : 8}
          viewBox="0 0 6 6"
        >
          <circle cx="3" cy="3" r="3" fill={disabled ? componentsWebColors.neutral[200] : baseColors.white} />
        </svg>
      </div>
    )
  }
)

RadioButton.displayName = 'RadioButton'
